import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import type { SelectionState } from '@/infra/selection-state'
import {
  DataTable,
  type DataTableColumn,
  DataTableRowSkeleton,
} from '@/components/DataTable'
import {
  AppliedFiltersContainer,
  FilterButtonContainer,
} from '@/components/DataTable/FilterBarContainer'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import { links } from '@/utils/link'
import { Id } from '@/infra/id'
import { sharedSavedFilterId } from '@/features/jobs/list/saved-job-filters'
import { match } from '@/utils/pattern-match-util'
import { type JobManagementViewModel } from '@/features/jobs/list/JobManagementViewModel'
import {
  JobList,
  SaveAsTab,
  SharedTabBanner,
} from '@/features/jobs/list/components/JobManagement/JobList'
import { type JobListTabsViewModel } from '@/features/jobs/list/JobListTabsViewModel'
import { DisplayMenuContainer } from './DisplayMenuContainer'
import { ExportJobsDialogContainer } from './ExportJobsDialogContainer'
import { JobListCellContainer } from './JobListCellContainer'
import { MoreMenuContainer } from './MoreMenuContainer'
import { SavedFilterTabsContainer } from './SavedFilterTabsContainer'

export interface JobListContainerProps {
  vm: JobManagementViewModel
}

export const JobListContainer = observer(function JobListContainer({
  vm,
}: JobListContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  const list = vm.jobListPageVm
  return (
    <JobList
      corporateMode={shellInfo.corporateMode}
      tabs={<SavedFilterTabsContainer vm={list.tabsVm} />}
      sharedTabBanner={<SharedTabBannerContainer vm={list.tabsVm} />}
      searchText={vm.searcher.searchText.value}
      onSearchChange={vm.searcher.searchText.change}
      isProPerspective={list.perspective === 'Pro'}
      filterButton={
        <FilterButtonContainer
          vm={vm.searcher.filters}
          editorTestId="job"
          testId="job-filter"
        />
      }
      displayMenu={<DisplayMenuContainer vm={vm} />}
      selection={
        list.canSelect
          ? {
              isShowing: list.showSelection,
              onToggleShowing: vm.toggleSelecting,
            }
          : undefined
      }
      onExport={list.openExportDialog}
      moreMenu={<MoreMenuContainer vm={vm} />}
      appliedFilters={
        <AppliedFiltersContainer
          vm={vm.searcher.filters}
          actions={
            <SaveAsTab
              hint={list.tabsVm.addTabHint}
              onSave={list.tabsVm.saveAsTab}
            />
          }
          editorTestId="job"
          testId="job-filter"
        />
      }
      isEmpty={vm.showEmptyView}
      onClearSearchFilter={vm.searcher.clearFilters}
      onSetJobScope={list.isScopedToMine ? list.showAllJobs : undefined}
      exportDialog={<ExportJobsDialogContainer vm={list} />}
    >
      <JobListTableContainer vm={vm} />
    </JobList>
  )
})

export const JobListTableContainer = observer(function JobListTableContainer({
  vm,
}: JobListContainerProps) {
  const list = vm.jobListPageVm
  const selection = vm.selectionVm

  // DataTable is not an observer, so everything it reads per row is
  // dereferenced here.
  const rows = vm.filteredJobs.map((job) => ({
    job,
    href: !job.archived ? links.job(job.id) : undefined,
    testId: 'job-list-item-' + job.name,
    openTicket: vm.getLatestOpenTicketForJob(job),
    isSelected: list.showSelection && selection.isSelectedForActions(job),
  }))

  const columns = list.visibleColumns.map(
    (column): DataTableColumn<(typeof rows)[number]> => ({
      id: column.id,
      header: column.header(list.perspective),
      width: list.widthOf(column),
      alignRight: column.alignRight,
      sortDirection: column.sortField
        ? list.sortDirectionOf(column)
        : undefined,
      onSort: () => list.toggleSort(column),
      linked: column.locked,
      locked: column.locked,
      render: (row) => (
        <JobListCellContainer
          id={column.id}
          job={row.job}
          perspective={list.perspective}
          openTicket={row.openTicket}
        />
      ),
    }),
  )

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => Id.assert(row.job.id)}
      rowHref={(row) => row.href}
      rowTestId={(row) => row.testId}
      selection={
        list.showSelection
          ? {
              all: match<SelectionState, boolean | 'indeterminate'>(
                selection.jobActionSelectionState,
                {
                  ['AllSelected']: () => true,
                  ['NoneSelected']: () => false,
                  ['SomeSelected']: () => 'indeterminate',
                },
              ),
              onToggleAll: selection.toggleAllSelectedJobsForActions,
              isSelected: (row) => row.isSelected,
              onToggle: (row) =>
                selection.toggleJobSelectionForActions(row.job),
              isPending: selection.isSelectAllPending,
            }
          : undefined
      }
      onResize={list.setWidth}
      onResizeEnd={list.save}
      onReorder={list.moveColumn}
      isSkeleton={vm.searcher.isFetchingInitial}
      footer={
        <SkeletonListInfiniteScrollTrigger
          scroller={vm.searcher.scroller}
          maxSkeletons={10}
        >
          <DataTableRowSkeleton />
        </SkeletonListInfiniteScrollTrigger>
      }
    />
  )
})

interface SharedTabBannerContainerProps {
  vm: JobListTabsViewModel
}

const SharedTabBannerContainer = observer(function SharedTabBannerContainer({
  vm,
}: SharedTabBannerContainerProps) {
  const shared = vm.sharedTab
  if (!shared || vm.selectedTabId !== sharedSavedFilterId) {
    return null
  }

  return (
    <SharedTabBanner
      sharedBy={shared.sharedBy}
      name={shared.name}
      addHint={vm.addTabHint}
      onAdd={vm.addSharedTab}
      onDismiss={vm.dismissSharedTab}
    />
  )
})
